"use client";

import { getPlatformOrigin } from "@/configs";
import PublisherConfig from "@/configs/publisher.json";
import { AUTH_CLEARED_EVENT, consumeAuthTokenFromUrl } from "@/lib/auth-cookie";
import { SITE_SHELL } from "@/lib/layout";
import { OPEN_SIGNIN_EVENT } from "@/lib/open-signin";
import { usePathname } from "next/navigation";
import type React from "react";
import { useEffect, useState } from "react";
import { AccountModal } from "./account-modal";
import { SiteHeader } from "./site-header";
import { SubscribeModal, type SubscribeModalView } from "./subscribe-modal";
import { SubscriptionProvider } from "./subscription-provider";

function Footer() {
  const platformOrigin = getPlatformOrigin() || "https://cantabook.com";

  return (
    <footer className="w-full border-t border-border/40 bg-muted/20 py-8 mt-24">
      <div className={`${SITE_SHELL} flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 text-center`}>
        <p className="text-xs text-muted-foreground">
          Copyright &copy; <span className="font-semibold text-foreground">{PublisherConfig.profile.name}</span> {new Date().getFullYear()}.
          All rights reserved.
        </p>
        <span className="hidden sm:inline text-xs text-muted-foreground" aria-hidden>
          ·
        </span>
        <p className="text-xs text-muted-foreground">
          <a
            href={platformOrigin}
            className="font-medium text-foreground/80 hover:text-foreground underline-offset-2 hover:underline"
            rel="noopener noreferrer"
          >
            Made with Cantabook
          </a>
        </p>
      </div>
    </footer>
  );
}

export default function PageWrapper({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const [isSubscribeOpen, setIsSubscribeOpen] = useState(false);
  const [subscribeView, setSubscribeView] = useState<SubscribeModalView>("subscribe");
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [authVersion, setAuthVersion] = useState(0);
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (consumeAuthTokenFromUrl()) {
        setAuthVersion((value) => value + 1);
      }
      const params = new URLSearchParams(window.location.search);
      if (params.get("subscribe") === "true") {
        setSubscribeView("subscribe");
        setIsSubscribeOpen(true);
      }
      if (params.get("signin") === "true") {
        setSubscribeView("signin");
        setIsSubscribeOpen(true);
      }
    }

    const handleOpenSubscribe = () => {
      setSubscribeView("subscribe");
      setIsSubscribeOpen(true);
    };
    const handleOpenSignIn = () => {
      setSubscribeView("signin");
      setIsSubscribeOpen(true);
    };
    const handleOpenSupport = () => {
      setSubscribeView("support");
      setIsSubscribeOpen(true);
    };
    const handleAuthCleared = () => {
      setAuthVersion((value) => value + 1);
      setIsAccountOpen(false);
    };
    window.addEventListener("open-subscribe-modal", handleOpenSubscribe);
    window.addEventListener("open-support-modal", handleOpenSupport);
    window.addEventListener(OPEN_SIGNIN_EVENT, handleOpenSignIn);
    window.addEventListener(AUTH_CLEARED_EVENT, handleAuthCleared);
    return () => {
      window.removeEventListener("open-subscribe-modal", handleOpenSubscribe);
      window.removeEventListener("open-support-modal", handleOpenSupport);
      window.removeEventListener(OPEN_SIGNIN_EVENT, handleOpenSignIn);
      window.removeEventListener(AUTH_CLEARED_EVENT, handleAuthCleared);
    };
  }, [pathname]);

  return (
    <SubscriptionProvider authVersion={authVersion}>
      <div className="flex flex-col min-h-screen justify-between relative bg-background">
        <SiteHeader authVersion={authVersion} onOpenAccount={() => setIsAccountOpen(true)} />
        <div className="flex flex-col flex-1 w-full">
          <main className="flex-1 w-full">{children}</main>
        </div>
        <Footer />
        <SubscribeModal
          isOpen={isSubscribeOpen}
          initialView={subscribeView}
          onClose={() => setIsSubscribeOpen(false)}
          onSignInSuccess={() => setAuthVersion((value) => value + 1)}
        />
        <AccountModal
          isOpen={isAccountOpen}
          onClose={() => setIsAccountOpen(false)}
          onSignOut={() => setAuthVersion((value) => value + 1)}
        />
      </div>
    </SubscriptionProvider>
  );
}
